import React, {
  useCallback,
  useEffect, useMemo, useState,
} from 'react';
import Box from '@mui/material/Box';
import { translations } from 'src/constants/translations';
import { ICrumbs } from 'src/components/profileComponents/Breadcrumbs';
import { pathList } from 'src/containers/Profile/routeList';
import { appIcon } from 'src/assets';
import InputAdornment from '@mui/material/InputAdornment';
import { useSelector } from 'react-redux';
import { requestSelectors } from 'src/store/requests/selectors';
import { useDebounce } from 'src/hooks';
import { useUsersManage } from 'src/components/profileComponents/Users/hooks/useUsersManage';
import { IManageUser, IUsersManageStatuses } from 'src/typings/usersManage';
import { buildGetParamsFromObject } from 'src/helpers/buildGetParams';
import { AppTable } from 'src/components/AppTable';
import { Pagination, Stack } from '@mui/material';
import { useTable } from 'src/hooks/table/useTable';
import {
  GridColDef, GridRenderCellParams, GridTreeNodeWithRender,
} from '@mui/x-data-grid';
import { allOption, RolesMap, usersTableRows } from 'src/components/profileComponents/Users/constants';
import { IOSSwitch } from 'src/components/SwitchIos';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { Hydra } from 'src/api/hydraResponse';
import { useGlobalGrid } from 'src/hooks/app/use-global-grid';
import { UsersPageWrapper } from 'src/components/profileComponents/Users/components/UsersPageWrapper';
import { UserInput } from 'src/components/profileComponents/Users/components/UserInput';
import { UserAutocomplete } from 'src/components/profileComponents/Users/components/UserAutocomplete';
import { TypeOfRole } from 'src/typings/user';
import { useHistory } from 'react-router-dom';
import { useUser } from 'src/hooks/user';
import { useStyles as useCommonUsers } from 'src/components/profileComponents/Common/Users';
import { useStyles } from './styles';

export const ManageUsers: React.FC = () => {
  const [searchState, setSearchState] = useState('');
  const [page, setPage] = useState<number | undefined>();
  const [totalItems, setTotalItems] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(1);
  const [statusUpdateInProgress, setStatusUpdateInProgress] = useState<string[]>([]);
  const debouncedSearchTerm = useDebounce(searchState, 500);
  const {
    initialUsersDataIsLoading,
    filteredDataIsLoading,
    initialUsersData,
    usersFilteredData,
    getParams,
    setGetParams,
    filteredDataRefetch,
    changeComplexStatus,
  } = useUsersManage();
  const [selectedDeveloperId, setSelectedDeveloperId] = useState<number>();
  const [selectedRoleId, setSelectedRoleId] = useState<number>();
  const [selectedStatusId, setSelectedStatusId] = useState<IUsersManageStatuses>();
  const {
    currentUserRole, getEditRoles,
  } = useUser();
  const user = useSelector(requestSelectors.user.getUserData);
  const { globalGrid, getGridsWidth } = useGlobalGrid();
  const {
    apiRef,
    rows,
    columns,
    setRows,
    setColumns,
    applyApi,
  } = useTable();
  const history = useHistory();
  const styles = useStyles(getGridsWidth);
  const commonUsersStyles = useCommonUsers();

  const usersList = useMemo(() => {
    const tmpEntity: Hydra<IManageUser[]> = usersFilteredData || initialUsersData?.users || [] as unknown as Hydra<IManageUser[]>;
    setItemsPerPage(tmpEntity['hydra:itemsPerPage']);
    setTotalItems(tmpEntity['hydra:totalItems']);
    return usersFilteredData?.['hydra:member'] || initialUsersData?.users['hydra:member'] || [];
  }, [usersFilteredData, initialUsersData]);

  const firstDeveloper = initialUsersData?.filters.developers[0];
  const preselectedDeveloperOption = useMemo(() => {
    return currentUserRole === TypeOfRole.superadmin ? allOption : firstDeveloper ? ({ label: firstDeveloper.name, value: firstDeveloper.id }) : undefined;
  }, [user, firstDeveloper]);

  const handleChangeStatus = useCallback((params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>, status: IUsersManageStatuses) => {
    setStatusUpdateInProgress([...statusUpdateInProgress, params.row.id]);
    const updatedStatus = status === IUsersManageStatuses.ENABLED ? IUsersManageStatuses.DISABLED : IUsersManageStatuses.ENABLED;
    changeComplexStatus(params.row.id, updatedStatus)
      .then(() => {
        statusUpdateInProgress.splice(statusUpdateInProgress.indexOf(params.row.id), 1);
        setStatusUpdateInProgress(statusUpdateInProgress);
        params.row.status = updatedStatus;
        params.api.setEditCellValue({ id: params.id, field: params.field, value: params.row });
      });
  }, [changeComplexStatus, setStatusUpdateInProgress]);

  const openEditPage = useCallback((userId: string, role: TypeOfRole) => {
    if (getEditRoles().includes(role)) {
      history.push(`/profile/${pathList.users}/edit/${userId}`);
    }
  }, [history]);

  useEffect(() => {
    const tempColumns: GridColDef[] = usersTableRows.map((field) => ({
      field: field.id.toString(),
      headerName: field.headerName,
      type: 'string',
      headerAlign: field.headerAlign,
      align: field.align,
      flex: field.flex,
      minWidth: getGridsWidth(field.width || 0),
      display: 'flex',
      renderHeader: (params) => {
        if (field.subHeaderName) {
          return (
            <>
              <Box display="flex" flexDirection="column" alignItems="flex-start" gap="4px">
                <span className={commonUsersStyles.text}>{params.colDef.headerName}</span>
                <span className={commonUsersStyles.subText}>{field.subHeaderName}</span>
              </Box>
            </>
          );
        }
        return <div className={commonUsersStyles.text}>{params.colDef.headerName}</div>;
      },
      renderCell: (params) => {
        const fieldValue = params.row[field.fieldName];
        switch (field.fieldName) {
          case 'username':
            return (
              <>
                <Box display="flex" flexDirection="column" alignItems="flex-start" gap="4px">
                  <span className={commonUsersStyles.text}>{fieldValue}</span>
                  <span className={commonUsersStyles.subText}>{params.row.email}</span>
                </Box>
              </>
            );
          case 'role':
            return <div>{params.row.role?.name ? RolesMap[params.row.role.name as keyof typeof RolesMap] : ''}</div>;
          case 'projects':
            return (
              <div>
                <span>{params.row.complexesLinkedCount}</span>
                /
                <span>{params.row.complexesTotalCount}</span>
              </div>
            );
          case 'status':
            return (
              <Box display="flex" justifyContent="flex-end" alignItems="center" gap="8px">
                <span className={commonUsersStyles.statusText}>{String(fieldValue).toLowerCase()}</span>
                <span>
                  { statusUpdateInProgress.includes(params.row.id) ? (
                    <Box display="flex" justifyContent="center" alignItems="center" width="52px" height="36px">
                      <CircularProgress size="30px" />
                    </Box>
                  ) : (
                    <IOSSwitch
                      checked={fieldValue === IUsersManageStatuses.ENABLED}
                      classes={{
                        switchBase: commonUsersStyles.switchBase,
                        track: commonUsersStyles.track,
                        checked: commonUsersStyles.checked,
                      }}
                      onChange={() => handleChangeStatus(params, fieldValue)}
                    />
                  ) }
                </span>
                <span>
                  <IconButton
                    color="primary"
                    size="large"
                    style={{
                      padding: 0,
                      opacity: params.row.role.name === TypeOfRole.superadmin ? '.3' : '1',
                    }}
                    disabled={!getEditRoles().includes(params.row.role.name)}
                    onClick={() => openEditPage(params.row.id, params.row.role.name)}
                  >
                    <img src={appIcon.iconEdit.default} alt="edit statuses" />
                  </IconButton>
                </span>
              </Box>
            );
          default: return <Box display="flex" alignItems="flex-start">{fieldValue}</Box>;
        }
      },
    })) || [];
    setColumns(tempColumns);
  }, [initialUsersData, selectedDeveloperId, preselectedDeveloperOption, statusUpdateInProgress, apiRef, globalGrid]);

  useEffect(() => {
    applyApi();
    // @ts-ignore
    setRows(usersList);
  }, [usersList]);

  useEffect(() => {
    const params = buildGetParamsFromObject({
      username: debouncedSearchTerm.trim().toString().toLowerCase() || '',
      developers: selectedDeveloperId?.toString() || '',
      role: selectedRoleId?.toString() || '',
      status: selectedStatusId?.toString() || '',
      page: page?.toString() || '',
    });
    setGetParams(params);
  }, [debouncedSearchTerm, selectedDeveloperId, selectedRoleId, selectedStatusId, initialUsersData, page]);

  useEffect(() => {
    filteredDataRefetch();
  }, [getParams]);

  const statusCount = useMemo(() => {
    const result = {
      enabled: 0,
      disabled: 0,
    };
    usersList.forEach((item) => {
      if (item.status === IUsersManageStatuses.ENABLED) result.enabled += 1;
      if (item.status === IUsersManageStatuses.DISABLED) result.disabled += 1;
    });
    return result;
  }, [usersList]);

  const options = useMemo(() => {
    const temp = {
      developer: initialUsersData?.filters.developers.map((developer) => ({ label: developer.name, value: developer.id })) || [],
      // @ts-ignore: nextLine
      role: initialUsersData?.filters.roles.map((role) => ({ label: RolesMap[role.name], value: role.id })) || [],
      status: initialUsersData?.filters.statuses.map((role) => ({ label: `${role.toLowerCase()} (${role === IUsersManageStatuses.ENABLED ? statusCount?.enabled : statusCount.disabled})`, value: role })) || [],
    };
    // @ts-ignore
    temp.developer.unshift(allOption);
    // @ts-ignore
    temp.status.unshift(allOption);
    // @ts-ignore
    temp.role.unshift(allOption);
    return temp;
  }, [initialUsersData, statusCount]);

  const onChange = (data: string) => {
    setSearchState(data);
  };

  const crumbs: ICrumbs[] = [
    {
      name: translations.usersLink.toUpperCase(),
      url: `/profile/${pathList.users}`,
    },
    {
      name: translations.manageUsersLink,
    },
  ];

  const CustomPagination = () => {
    const pageCount = Math.ceil(totalItems / itemsPerPage);

    return (
      <Box sx={{ p: 1 }}>
        { !!rows.length && (
          <Pagination
            count={pageCount}
            siblingCount={0}
            boundaryCount={-2}
            defaultPage={1}
            page={page || 1}
            onChange={(e, value) => {
              if (value > 0) {
                setPage(value);
              }
            }}
            renderItem={(params) => {
              if (
                params.type === 'start-ellipsis' || params.type === 'end-ellipsis') {
                return null;
              }
              return (
                <>
                  { params.type === 'previous' && (
                    <Box
                      className={`chevron chevron-left ${params.disabled ? 'disabled' : ''}`}
                      style={{
                        background: `url(${appIcon.iconChevronRightThin.default}) 55% center no-repeat`,
                      }}
                      onClick={(e) => {
                        if (!params.disabled) params.onClick(e);
                      }}
                    />
                  ) }
                  { params.type === 'page' && params.selected && (
                    <div className="currentPage">{ `Page ${page || 1} of ${pageCount}` }</div>
                  ) }
                  { params.type === 'next' && (
                    <Box
                      className={`chevron chevron-right ${params.disabled ? 'disabled' : ''}`}
                      style={{
                        background: `url(${appIcon.iconChevronRightThin.default}) 55% center no-repeat`,
                      }}
                      onClick={(e) => {
                        if (!params.disabled) params.onClick(e);
                      }}
                    />
                  ) }
                </>
              );
            }}
            color="standard"
          />
        ) }
      </Box>
    );
  };

  function noRowsOverlayFunc() {
    return (
      <>
        <Stack height="100%" alignItems="center" justifyContent="center" minHeight="50px" fontSize="18px" fontWeight="500">
          User not found
        </Stack>
      </>
    );
  }

  return (
    <UsersPageWrapper
      crumbs={crumbs}
      title={currentUserRole !== TypeOfRole.superadmin ? (
        `MANAGE ${firstDeveloper?.name} USERS`
      ) : 'MANAGE USERS'}
    >
      {preselectedDeveloperOption ? (
        <>
          <div className={styles.searchSection}>
            <form>
              <div className={styles.searchWrapper}>
                <div className={styles.search}>
                  <UserInput
                    startAdornment={(
                      <InputAdornment position="start">
                        <img src={appIcon.iconSearch.default} alt="icon search" />
                      </InputAdornment>
                    )}
                    label={translations.managersSearchPlaceholder}
                    placeholder="Enter name or email"
                    name="search"
                    value={searchState}
                    onChange={(e) => onChange(e.target.value)}
                  />
                </div>
                <div className={styles.developer}>
                  <UserAutocomplete
                    label={translations.developer}
                    name="developer"
                    defaultValue={preselectedDeveloperOption}
                    disabled={options.developer.length === 1}
                    options={options.developer}
                    onChange={(e) => {
                      setSelectedDeveloperId(e?.value);
                      setPage(1);
                    }}
                  />
                </div>
                <div className={styles.role}>
                  <UserAutocomplete
                    label={translations.role}
                    name="role"
                    defaultValue={allOption}
                    options={options.role}
                    onChange={(e) => {
                      setSelectedRoleId(e?.value);
                      setPage(1);
                    }}
                  />
                </div>
                <div className={styles.status}>
                  <UserAutocomplete
                    label={translations.status}
                    name="status"
                    defaultValue={allOption}
                    options={options.status}
                    onChange={(e) => {
                      setSelectedStatusId(e?.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>
            </form>
          </div>
          <div className={commonUsersStyles.tableSection}>
            { (filteredDataIsLoading || initialUsersDataIsLoading || !apiRef?.current)
              ? (
                <Box
                  width="100%"
                  height="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <CircularProgress />
                </Box>
              ) : (
                <AppTable
                  slots={{
                    noRowsOverlay: noRowsOverlayFunc,
                    pagination: CustomPagination,
                  }}
                  autoHeight
                  autosizeOnMount
                  apiRef={apiRef}
                  disableColumnFilter
                  disableColumnMenu
                  disableColumnSorting
                  disableColumnResize
                  disableRowSelectionOnClick
                  columns={columns}
                  rows={rows}
                  rowHeight={76}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-container--top [role=row]': {
                      background: 'unset',
                    },
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '16px',
                      fontWeight: '500',
                      lineHeight: '20px',
                      padding: 0,
                    },
                    '& .MuiDataGrid-columnHeader': {
                      padding: 0,
                    },
                  }}
                />
              ) }
          </div>
        </>
      ) : (
        <div className={styles.loaderWrapper}>
          <CircularProgress />
        </div>
      )}
    </UsersPageWrapper>
  );
};
